import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  ImageList,
  ImageListItem,
  Dialog,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const galleryImages = [
  {
    src: '/images/gallery/wall-cleaning.jpeg',
    title: 'Wall Cleaning',
    category: 'Residential',
  },
  {
    src: '/images/gallery/balcony-cleaning.jpeg',
    title: 'Balcony Cleaning in Kampala',
    category: 'Residential',
  },
  {
    src: '/images/gallery/balcony-view.jpeg',
    title: 'High-Rise Cleaning Services',
    category: 'Commercial',
  },
  {
    src: '/images/gallery/team-floor.jpeg',
    title: 'Team Floor Cleaning',
    category: 'Residential',
  },
  {
    src: '/images/gallery/floor-cleaning.jpeg',
    title: 'Commercial Floor Maintenance',
    category: 'Commercial',
  },
  {
    src: '/images/gallery/team-uniforms.jpeg',
    title: 'Our Professional Team',
    category: 'Team',
  },
  {
    src: '/images/gallery/training-session.jpeg',
    title: 'Cleaner Training Session',
    category: 'Training',
  },
  {
    src: '/images/gallery/training-class.jpeg',
    title: 'Professional Training Class',
    category: 'Training',
  },
  {
    src: '/images/gallery/building-cleaning.jpeg',
    title: 'Building Exterior Cleaning',
    category: 'Commercial',
  },
];

const categories = ['All', 'Residential', 'Commercial', 'Team', 'Training'];

const Gallery = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredImages =
    activeCategory === 'All'
      ? galleryImages
      : galleryImages.filter((img) => img.category === activeCategory);

  const currentIndex = selectedImage
    ? filteredImages.findIndex((img) => img.src === selectedImage.src)
    : -1;

  const handlePrev = () => {
    if (currentIndex > 0) setSelectedImage(filteredImages[currentIndex - 1]);
  };
  const handleNext = () => {
    if (currentIndex < filteredImages.length - 1)
      setSelectedImage(filteredImages[currentIndex + 1]);
  };

  return (
    <>
      <SEO
        title="Gallery - Blossom City Cleaning Services | Our Work in Kampala"
        description="See our professional cleaning work across Kampala. From residential deep cleaning to commercial building maintenance, view real results from Blossom City Cleaning Services."
        keywords="cleaning gallery Kampala, cleaning results Uganda, professional cleaning photos, before after cleaning"
        url="https://blossomcleaningservices.com/gallery"
      />
      <Header />

      {/* Hero */}
      <Box
        sx={{
          pt: { xs: 12, md: 16 },
          pb: { xs: 4, md: 6 },
          background: 'linear-gradient(135deg, #22335F 0%, #366495 100%)',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <Typography
              variant={isMobile ? 'h4' : 'h2'}
              sx={{ fontWeight: 800, mb: 2 }}
            >
              Our Work Gallery
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.85, maxWidth: 600, mx: 'auto' }}
            >
              Real results from our professional cleaning team across Kampala
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Filter Chips */}
      <Box sx={{ py: 3, backgroundColor: 'background.paper' }}>
        <Container>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
          >
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                onClick={() => setActiveCategory(cat)}
                sx={{
                  fontWeight: 600,
                  px: 1,
                  backgroundColor:
                    activeCategory === cat ? '#49914F' : 'transparent',
                  color: activeCategory === cat ? 'white' : 'text.primary',
                  border: `1px solid ${activeCategory === cat ? '#49914F' : '#ccc'}`,
                  '&:hover': {
                    backgroundColor:
                      activeCategory === cat ? '#3d7a43' : 'rgba(0,0,0,0.04)',
                  },
                }}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Gallery Grid */}
      <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: 'background.default' }}>
        <Container>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ImageList
                variant="masonry"
                cols={isMobile ? 1 : isTablet ? 2 : 3}
                gap={16}
              >
                {filteredImages.map((item, index) => (
                  <ImageListItem
                    key={item.src}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 3,
                      overflow: 'hidden',
                      '&:hover img': {
                        transform: 'scale(1.05)',
                      },
                    }}
                    onClick={() => setSelectedImage(item)}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                    >
                      <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden' }}>
                        <img
                          src={item.src}
                          alt={item.title}
                          loading="lazy"
                          style={{
                            width: '100%',
                            display: 'block',
                            borderRadius: 12,
                            transition: 'transform 0.4s ease',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background:
                              'linear-gradient(transparent, rgba(0,0,0,0.7))',
                            p: 2,
                            borderRadius: '0 0 12px 12px',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: 'white', fontWeight: 600 }}
                          >
                            {item.title}
                          </Typography>
                          <Chip
                            label={item.category}
                            size="small"
                            sx={{
                              mt: 0.5,
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              color: 'white',
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                      </Box>
                    </motion.div>
                  </ImageListItem>
                ))}
              </ImageList>
            </motion.div>
          </AnimatePresence>
        </Container>
      </Box>

      {/* Lightbox */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0,0,0,0.95)',
            boxShadow: 'none',
            borderRadius: 2,
            m: 1,
          },
        }}
      >
        {selectedImage && (
          <Box sx={{ position: 'relative', textAlign: 'center' }}>
            <IconButton
              onClick={() => setSelectedImage(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'white',
                zIndex: 2,
              }}
            >
              <CloseIcon />
            </IconButton>
            {currentIndex > 0 && (
              <IconButton
                onClick={handlePrev}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  zIndex: 2,
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}
            {currentIndex < filteredImages.length - 1 && (
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  zIndex: 2,
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              style={{
                maxWidth: '90vw',
                maxHeight: '85vh',
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="body1"
              sx={{ color: 'white', py: 2, fontWeight: 600 }}
            >
              {selectedImage.title}
            </Typography>
          </Box>
        )}
      </Dialog>

      <Footer />
    </>
  );
};

export default Gallery;
